/**
 * 재고관리 '입출고내역' 탭 — 전체 품목의 입·출고를 시간순으로.
 *
 * 개별 품목 모달(MovementDetailModal)과 같은 형태에 '품목' 칼럼을 더한 전체 화면 표.
 *   - 입고/출고 빠른 필터 (전체·입고·출고)
 *   - 검색바 → 품목·매입처·납품처 텍스트 검색
 *   - 컬럼 헤더 클릭 → 정렬 / ▼ → 값 필터 (품목·구분·날짜·수량·매입처·납품처·잔여재고)
 *
 * 잔여재고(balance)는 '그 품목'의 그 시점 잔량(품목별 입·출고 누적). 기본 정렬 = 날짜 내림차순(최신 위).
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as purchaseApi from '../lib/purchase-api';
import type { AllMovementRow } from '../lib/types';
import {
  type Column,
  ColumnHeader,
  applyColState,
  useColState,
} from './DataTable';
import { SearchBar } from './SearchBar';
import { ResetButton } from './ResetButton';
import { KindBadge } from './MfgBadge';
import { LoadingLines } from './ui/Skeleton';

type KindFilter = 'all' | '입고' | '출고';

export function MovementHistoryTab() {
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [q, setQ] = useState('');
  const colState = useColState();

  const query = useQuery({
    queryKey: ['purchases', 'all-movements'],
    queryFn: () => purchaseApi.getAllMovements(),
    staleTime: 30_000,
  });
  const allRows = query.data ?? [];

  const columns = useMemo<Column<AllMovementRow>[]>(
    () => [
      {
        key: 'itemName',
        label: '품목',
        type: 'text',
        accessor: (r) => r.itemName ?? r.itemCode ?? '',
        render: (r) => (
          <span className="font-medium text-slate-900">
            {r.itemName ?? r.itemCode ?? '-'}
            {r.itemGroup && (
              <span className="ml-1 text-[11px] text-slate-500">{r.itemGroup}</span>
            )}
          </span>
        ),
      },
      {
        key: 'spec',
        label: '규격',
        type: 'text',
        // ⭐매입등록에서 **직접 작성한 규격**을 먼저 보여준다 (2026-07-31, v2.039).
        //   품목마스터 규격은 '기타' 계열에서 '직접작성' 같은 안내문이라 그 건이 무엇인지
        //   알려주지 못한다 — 운영 UNK-0012 는 355행 전부 매입 규격이 마스터와 다르다.
        //   직접 작성한 규격이 없을 때만 예전처럼 마스터 규격으로 떨어진다.
        accessor: (r) => r.itemSpec ?? r.spec ?? '',
        render: (r) => {
          const shown = r.itemSpec ?? r.spec;
          return shown ? (
            <span className={r.itemSpec ? 'text-slate-700' : 'text-slate-500'}>{shown}</span>
          ) : (
            <span className="text-slate-300">-</span>
          );
        },
      },
      {
        key: 'kind',
        label: '구분',
        type: 'text',
        align: 'center',
        // 제조 행은 '입고 🔧제조 / 출고 🔧투입' 으로 값필터에서도 따로 골라진다.
        accessor: (r) => (r.mfg ? `${r.kind} ${r.kind === '입고' ? '🔧제조' : '🔧투입'}` : r.kind),
        sortAccessor: (r) => (r.kind === '입고' ? 0 : 1),
        render: (r) => <KindBadge kind={r.kind} mfg={r.mfg} mfgNo={r.mfgNo} />,
      },
      {
        key: 'date',
        label: '날짜',
        type: 'date',
        accessor: (r) => r.date,
      },
      {
        key: 'qty',
        label: '수량',
        type: 'number',
        align: 'right',
        accessor: (r) => (r.kind === '입고' ? r.qty : -r.qty),
        render: (r) => (
          <span className={`num font-semibold ${r.kind === '입고' ? 'text-emerald-700' : 'text-orange-700'}`}>
            {r.kind === '입고' ? '+' : '−'}
            {r.qty}
          </span>
        ),
      },
      {
        key: 'supplier',
        label: '매입처',
        type: 'text',
        accessor: (r) => r.supplier ?? '',
        render: (r) =>
          r.supplier ? (
            <span>
              {r.supplier}
              {r.kind === '입고' && r.salesId && (
                <span className="ml-1 text-[11px] text-blue-600">{r.salesId}</span>
              )}
            </span>
          ) : (
            <span className="text-slate-300">-</span>
          ),
      },
      {
        key: 'customer',
        label: '납품처',
        type: 'text',
        // 제조투입 출고는 납품처가 없다 — 대신 어느 제조에 들어갔는지(제조번호)를 보여준다.
        accessor: (r) =>
          r.customer ?? (r.kind === '출고' ? (r.mfg ? (r.mfgNo ?? '제조투입') : '출고') : ''),
        render: (r) =>
          r.kind === '출고' ? (
            r.mfg ? (
              <span className="text-indigo-700 text-xs" title="이 제조에 원재료로 투입됨">
                {r.mfgNo ?? '제조투입'}
              </span>
            ) : (
              <span>
                {r.customer ?? '출고'}
                {r.salesId && <span className="ml-1 text-[11px] text-blue-600">{r.salesId}</span>}
              </span>
            )
          ) : (
            <span className="text-slate-300">-</span>
          ),
      },
      {
        key: 'balance',
        label: '잔여재고',
        type: 'number',
        align: 'right',
        accessor: (r) => r.balance,
        render: (r) => (
          <span className={`num font-semibold ${r.balance < 0 ? 'text-red-600' : 'text-slate-700'}`}>
            {r.balance}
          </span>
        ),
      },
    ],
    [],
  );

  // 입고/출고 빠른 필터 → 검색(품목·매입처·납품처) → 컬럼 정렬·값필터 순으로 적용.
  const rows = useMemo(() => {
    let r = allRows;
    if (kindFilter !== 'all') r = r.filter((x) => x.kind === kindFilter);
    const term = q.trim().toLowerCase();
    if (term) {
      // 품목 식별은 품목명·규격·그룹으로 — P/N(품목코드)으로는 검색하지 않음
      r = r.filter(
        (x) =>
          (x.itemName ?? '').toLowerCase().includes(term) ||
          // 직접 작성한 규격으로도 찾을 수 있어야 한다 — 화면에 보이는 값이 그것이다.
          (x.itemSpec ?? '').toLowerCase().includes(term) ||
          (x.spec ?? '').toLowerCase().includes(term) ||
          (x.itemGroup ?? '').toLowerCase().includes(term) ||
          (x.supplier ?? '').toLowerCase().includes(term) ||
          (x.customer ?? '').toLowerCase().includes(term),
      );
    }
    return applyColState(r, columns, colState);
  }, [allRows, kindFilter, q, columns, colState]);

  const filterCount = Object.keys(colState.filters).length;
  const inCount = allRows.filter((r) => r.kind === '입고').length;
  const outCount = allRows.filter((r) => r.kind === '출고').length;

  const KIND_BTN: { key: KindFilter; label: string }[] = [
    { key: 'all', label: `전체 ${allRows.length}` },
    { key: '입고', label: `입고 ${inCount}` },
    { key: '출고', label: `출고 ${outCount}` },
  ];

  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 flex flex-col">
      {/* 툴바 — 입고/출고 토글 + 검색 + 초기화 */}
      <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center gap-2 text-xs">
        <div className="flex rounded overflow-hidden border border-slate-300">
          {KIND_BTN.map((b) => (
            <button
              key={b.key}
              type="button"
              onClick={() => setKindFilter(b.key)}
              className={`btn btn-sm ${kindFilter === b.key ? 'btn-primary' : 'btn-secondary'}`}
            >
              {b.label}
            </button>
          ))}
        </div>
        <SearchBar
          value={q}
          onCommit={setQ}
          placeholder="🔍 품목 / 매입처 / 납품처 검색"
          className="w-full sm:w-72 min-w-0"
        />
        {(colState.sortKey || filterCount > 0 || q || kindFilter !== 'all') && (
          <ResetButton
            onClick={() => {
              colState.resetAll();
              setQ('');
              setKindFilter('all');
            }}
            label={`↺ 정렬·필터 초기화${filterCount > 0 ? ` (${filterCount}컬럼)` : ''}`}
            title="입고/출고·검색·컬럼 정렬/필터 모두 초기 상태로"
          />
        )}
        <span className="text-slate-500 whitespace-nowrap">표시 {rows.length}건</span>
        <span className="text-slate-400 ml-auto hidden md:inline">
          컬럼 헤더 클릭 → 정렬 / ▼ → 값 필터
        </span>
      </div>

      {/* 본문 — 스크롤 영역 (sticky 헤더) */}
      <div className="overflow-auto max-h-[calc(100vh-260px)]">
        {query.isLoading && (
          <div className="p-12 text-center text-slate-400 text-sm"><LoadingLines /></div>
        )}
        {query.isError && (
          <div className="p-12 text-center text-red-500 text-sm">
            오류: {(query.error as Error).message}
          </div>
        )}
        {query.data && (
          <table className="w-full text-[13px] border-collapse">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <tr>
                {columns.map((col) => (
                  <ColumnHeader key={col.key} column={col} baseRows={allRows} columns={columns} colState={colState} />
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="p-10 text-center text-slate-400 text-sm">
                    조건에 맞는 입·출고 내역이 없습니다.
                  </td>
                </tr>
              )}
              {rows.flatMap((r, i) => {
                const key = `${r.itemCode ?? r.itemName}-${r.kind}-${r.date}-${i}`;
                const main = (
                  <tr
                    key={key}
                    className={`border-b border-slate-100 hover:bg-slate-50 ${r.note ? '!border-b-0' : ''}`}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={`px-3 py-1.5 align-middle ${
                          col.align === 'right'
                            ? 'text-right'
                            : col.align === 'center'
                              ? 'text-center'
                              : 'text-left'
                        }`}
                      >
                        {col.render ? col.render(r) : (col.accessor(r) ?? '-')}
                      </td>
                    ))}
                  </tr>
                );
                if (!r.note) return [main];
                return [
                  main,
                  <tr key={`${key}-note`} className="border-b border-slate-100 bg-amber-50/40">
                    <td colSpan={columns.length} className="px-3 pb-2 pt-0">
                      <div className="text-xs text-amber-900 whitespace-pre-line break-all">
                        📝 {r.note}
                      </div>
                    </td>
                  </tr>,
                ];
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* 푸터 */}
      <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-500 rounded-b">
        💡 잔여재고 = 그 품목의 그 시점 잔량(품목별 입·출고 누적). 같은 날은 입고 → 출고 순.
      </div>
    </div>
  );
}
